import { type MenuElement, MenuItem } from "prosemirror-menu";
import type { Attrs, Node, NodeType } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { isHeadingNode, schema } from "../adapter/schema";
import { createChevronRightSvg } from "../icons";
import { toolbarPluginKey } from "../plugins/toolbar";
import { getSharedAttrs } from "../utils";

import { t } from "i18n/i18n";

/** Returns true if the selection spans multiple blocks with different types or heading levels. */
function selectionHasMultipleBlockLevels(state: EditorState): boolean {
  const { from, to } = state.selection;
  const keys = new Set<string>();
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isTextblock) return;
    const key = `${node.type.name}-${isHeadingNode(node) ? node.attrs.level : null}`;
    keys.add(key);
    if (keys.size > 1) return false;
  });
  return keys.size > 1;
}

/** Get the current block type label based on the editor state. */
function getCurrentBlockLabel(state: EditorState, short = false): string {
  if (selectionHasMultipleBlockLevels(state)) return t("worksheet.rich_text_editor.mixed");

  const { $from } = state.selection;
  const node = $from.parent;

  if (isHeadingNode(node)) {
    const { level } = node.attrs;
    return short ? `H${level}` : t("worksheet.rich_text_editor.heading", { level });
  }

  return t("worksheet.rich_text_editor.normal");
}

function nodeMatchesTarget(
  node: Node,
  nodeType: NodeType,
  typeSpecificAttrs: Attrs | null,
): boolean {
  if (node.type !== nodeType) return false;
  if (!typeSpecificAttrs) return true;
  return Object.entries(typeSpecificAttrs).every(([key, value]) => node.attrs[key] === value);
}

/** Creates a MenuItem that sets the block type, preserving shared attrs (align, indent). */
function blockTypeMenuItem(
  nodeType: NodeType,
  typeSpecificAttrs: Attrs | null,
  label: string,
  renderBtn: () => HTMLButtonElement,
): MenuItem {
  return new MenuItem({
    label,
    active(state) {
      if (selectionHasMultipleBlockLevels(state)) return false;
      return nodeMatchesTarget(state.selection.$from.parent, nodeType, typeSpecificAttrs);
    },
    run(state, dispatch, view) {
      const { selection, doc, tr } = state;
      let changed = false;
      doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        if (!nodeMatchesTarget(node, nodeType, typeSpecificAttrs)) {
          tr.setBlockType(pos, pos + node.nodeSize, nodeType, {
            ...getSharedAttrs(node),
            ...typeSpecificAttrs,
          });
          changed = true;
        }
        return false; // Never recurse into child nodes
      });
      if (!changed) return false;
      dispatch?.(tr.scrollIntoView().setMeta(toolbarPluginKey, { activePanelId: "main" }));
      view?.focus(); // keyboard nav: return focus to editor for continued typing
      return true;
    },
    render: renderBtn,
  });
}

/**
 * Create a menu item for a heading level.
 * @param level - The heading level (1–6)
 */
export function headingMenuItem(nodeType: NodeType, level: number): MenuItem {
  const headingLabel = t("worksheet.rich_text_editor.heading", { level });
  const headingLabelShort = `H${level}`;

  return blockTypeMenuItem(nodeType, { level }, headingLabel, () => {
    const btn = document.createElement("button");
    btn.className = "text-style-item flex h-7 items-center rounded px-2 whitespace-nowrap";

    const longLabel = document.createElement("span");
    longLabel.className = "hidden md:inline";
    longLabel.textContent = headingLabel;

    const shortLabel = document.createElement("span");
    shortLabel.className = "inline md:hidden";
    shortLabel.textContent = headingLabelShort;

    btn.append(longLabel, shortLabel);
    return btn;
  });
}

/** Create a menu item for "Normal" (paragraph) text style. */
export function normalMenuItem(nodeType: NodeType): MenuItem {
  const normalLabel = t("worksheet.rich_text_editor.normal");

  return blockTypeMenuItem(nodeType, null, normalLabel, () => {
    const btn = document.createElement("button");
    btn.className = "text-style-item flex h-7 items-center rounded px-2";
    btn.textContent = normalLabel;
    return btn;
  });
}

/** Returns all text style MenuItems for the text styles panel. */
export function textStyleItems(): MenuElement[] {
  return [
    normalMenuItem(schema.nodes.paragraph),
    headingMenuItem(schema.nodes.heading, 1),
    headingMenuItem(schema.nodes.heading, 2),
    headingMenuItem(schema.nodes.heading, 3),
    headingMenuItem(schema.nodes.heading, 4),
    headingMenuItem(schema.nodes.heading, 5),
    headingMenuItem(schema.nodes.heading, 6),
  ];
}

/** Creates a toolbar button that displays the current text style and opens the text styles panel on click. */
export function textStylePanelItem(): MenuElement {
  return {
    render(view: EditorView) {
      const btn = document.createElement("button");
      btn.className =
        "text-style-panel-item flex items-center justify-between gap-2 px-2 whitespace-nowrap";
      btn.title = t("worksheet.rich_text_editor.text_style");

      const longLabel = document.createElement("span");
      longLabel.className = "hidden md:inline";
      longLabel.textContent = getCurrentBlockLabel(view.state);

      const shortLabel = document.createElement("span");
      shortLabel.className = "inline md:hidden";
      shortLabel.textContent = getCurrentBlockLabel(view.state, true);

      const iconContainer = document.createElement("span");
      iconContainer.className =
        "flex-none flex items-center justify-center w-[8px] h-[8px] overflow-hidden";
      iconContainer.appendChild(createChevronRightSvg());
      btn.append(longLabel, shortLabel, iconContainer);

      btn.addEventListener("click", (event) => {
        const isKeyboard = event.detail === 0; // detail = click count
        view.focus(); // keeps :focus-within alive during dispatch; no-op for mouse
        view.dispatch(
          view.state.tr.setMeta(toolbarPluginKey, {
            activePanelId: "textStyle",
            shouldFocus: isKeyboard,
          }),
        );
      });

      return {
        dom: btn,
        update: (state: EditorState) => {
          longLabel.textContent = getCurrentBlockLabel(state);
          shortLabel.textContent = getCurrentBlockLabel(state, true);
          return true; // false would signal prosemirror-menu to hide the element; visibility is handled by withPanelVisibility
        },
      };
    },
  };
}
